import {
  BottomSheet,
  Card,
  List,
  ListItem,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { alpha, Box, CardContent, DialogContent, RadioGroup, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { RadioField } from "@src/appV2/lib/FormFields/RadioField/RadioField";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useCallback, useState } from "react";

import graduationCapImage from "./assets/images/graduationCap.png";
import {
  CONCORDE_PROGRAMS,
  SCHOLARSHIP_BOTTOM_SHEET_GRADIENT,
  SCHOLARSHIP_RESPONSES,
  type ScholarshipResponse,
} from "./constants";

interface ScholarshipBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  onSubmit: (response: ScholarshipResponse) => void;
}

export function ScholarshipBottomSheet(props: ScholarshipBottomSheetProps) {
  const { modalState, workerId, onSubmit } = props;
  const [selectedResponse, setSelectedResponse] = useState<ScholarshipResponse | "">("");

  useLogEffect(
    APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_BOTTOM_SHEET_VIEWED,
    { workerId },
    { enabled: modalState.modalIsOpen }
  );

  const handleConfirm = useCallback(() => {
    if (!selectedResponse) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_INTEREST_SUBMITTED, {
      workerId,
      response: selectedResponse,
    });

    onSubmit(selectedResponse);
    modalState.closeModal();
  }, [selectedResponse, workerId, onSubmit, modalState]);

  function handleDismiss(): void {
    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_BOTTOM_SHEET_DISMISSED, {
      workerId,
    });
  }

  function handleClose(): void {
    handleDismiss();
    modalState.closeModal();
  }

  const handleResponseChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedResponse(event.target.value as ScholarshipResponse);
  }, []);

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        minHeight: "90%",
        background: SCHOLARSHIP_BOTTOM_SHEET_GRADIENT,
        overflow: "hidden",
      }}
      header={
        <BottomSheetHeader
          onClose={() => {
            handleClose();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!selectedResponse}
            onClick={handleConfirm}
          >
            Confirm Selection
          </Button>
        </DialogFooter>
      }
      onClose={() => {
        handleDismiss();
      }}
    >
      <Box
        component="img"
        src={graduationCapImage}
        alt=""
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "70%",
          maxWidth: 320,
          aspectRatio: "1 / 1",
          objectFit: "contain",
          pointerEvents: "none",
          transform: "translate(20%, -25%)",
          opacity: 0.75,
        }}
      />
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", flex: 1, position: "relative" }}
      >
        <Stack gap={6}>
          <Text
            semibold
            variant="h2"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.primary })}
          >
            15% Off Tuition
          </Text>

          <Text
            variant="body1"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.secondary })}
          >
            <Text bold inline sx={(theme) => ({ color: theme.palette.text.primary })}>
              Coming soon,
            </Text>{" "}
            qualified professionals will get{" "}
            <Text bold inline sx={(theme) => ({ color: theme.palette.text.primary })}>
              15% off tuition
            </Text>{" "}
            at{" "}
            <Text bold inline sx={(theme) => ({ color: theme.palette.text.primary })}>
              Concorde University
            </Text>{" "}
            across all programs at 17 campuses nationwide, plus online degrees.
          </Text>
        </Stack>

        <Stack sx={{ marginTop: 8 }} gap={5}>
          <Text semibold variant="h5" sx={{ textAlign: "center" }}>
            Some of the Available Programs
          </Text>
          <List dense disablePadding sx={{ listStyleType: "disc", paddingLeft: 5 }}>
            {CONCORDE_PROGRAMS.map((program) => (
              <ListItem
                key={program}
                disableGutters
                disablePadding
                sx={{ display: "list-item", padding: 0, minHeight: 0 }}
              >
                <Text variant="body1">{program}</Text>
              </ListItem>
            ))}
          </List>
          <TextButton
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_SEE_ALL_PROGRAMS_CLICKED, {
                workerId,
              });
              window.open("https://www.concorde.edu/programs", "_blank");
            }}
          >
            See all programs and learn more
          </TextButton>
        </Stack>

        <Stack sx={{ flex: 1, justifyContent: "center", marginY: 8 }}>
          <Card
            variant="tertiary"
            sx={(theme) => ({
              ...(isDefined(theme.palette.background.tertiary) && {
                backgroundColor: alpha(theme.palette.background.tertiary, 0.9),
              }),
            })}
          >
            <CardContent>
              <Stack spacing={3}>
                <Text semibold variant="h6">
                  Interested in this scholarship?
                </Text>
                <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                  If so, we&apos;ll notify you when it&apos;s available.
                </Text>
                <RadioGroup value={selectedResponse} onChange={handleResponseChange}>
                  <RadioField
                    value={SCHOLARSHIP_RESPONSES.INTERESTED}
                    label={
                      <Text sx={(theme) => ({ color: theme.palette.intent?.success?.text })}>
                        Yes! I am interested!
                      </Text>
                    }
                  />
                  <RadioField
                    value={SCHOLARSHIP_RESPONSES.NOT_INTERESTED}
                    label={
                      <Text sx={(theme) => ({ color: theme.palette.intent?.error?.text })}>
                        No. I am not interested
                      </Text>
                    }
                  />
                </RadioGroup>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
